import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { User } from '../api/entities.js';
import ExportButton from '../components/common/ExportButton.jsx';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { ScrollArea } from "../components/ui/scroll-area.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { Skeleton } from "../components/ui/skeleton.jsx";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion, AnimatePresence } from "framer-motion";
import { 
  Plus, 
  Search, 
  Filter,
  Users as UsersIcon,
  User as UserIcon,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Mail,
  Phone,
  Send,
  MoreHorizontal,
  Edit2,
  Trash2,
  Eye,
  Clock,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  XCircleIcon,
  Building2,
  LayoutGrid,
  LayoutList,
  ArrowDownAZ,
  ArrowUpZA,
  UserPlus,
  SlidersHorizontal,
  Activity,
  CircleCheck,
  UserCog,
  CircleAlert,
  AlertOctagon,
  XCircleIcon as XIcon,
  RefreshCcw,
  ExternalLink,
  Settings,
  BarChart,
  Headphones,
  Briefcase,
  Key,
  KeyRound
} from 'lucide-react';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.jsx";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog.jsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog.jsx";
import { Label } from "../components/ui/label.jsx";

// Role configuration with colors and icons
const ROLE_CONFIG = {
  admin: {
    label: 'Administrador',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: <ShieldAlert className="h-3.5 w-3.5 mr-1" />,
    description: 'Acesso completo à plataforma, incluindo configurações do sistema'
  },
  manager: {
    label: 'Gerente',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: <ShieldCheck className="h-3.5 w-3.5 mr-1" />,
    description: 'Acesso à gestão de projetos, clientes e relatórios'
  },
  support: {
    label: 'Suporte',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: <Headphones className="h-3.5 w-3.5 mr-1" />,
    description: 'Acesso aos chamados e atendimento ao cliente'
  },
  dev: {
    label: 'Desenvolvedor',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    icon: <Briefcase className="h-3.5 w-3.5 mr-1" />,
    description: 'Acesso aos chamados de desenvolvimento e projetos'
  },
  client: {
    label: 'Cliente',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: <Building2 className="h-3.5 w-3.5 mr-1" />,
    description: 'Acesso limitado aos chamados próprios e informações do cliente'
  },
  user: {
    label: 'Usuário',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: <UserIcon className="h-3.5 w-3.5 mr-1" />,
    description: 'Acesso básico ao sistema'
  }
};

// Status configuration
const STATUS_CONFIG = {
  active: {
    label: 'Ativo',
    color: 'bg-green-100 text-green-800',
    icon: <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
  },
  inactive: {
    label: 'Inativo',
    color: 'bg-red-100 text-red-800',
    icon: <XCircleIcon className="h-3.5 w-3.5 mr-1" />
  },
  pending: {
    label: 'Pendente',
    color: 'bg-amber-100 text-amber-800',
    icon: <CircleAlert className="h-3.5 w-3.5 mr-1" />
  }
};

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filters, setFilters] = useState({
    role: 'all',
    search: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [showStats, setShowStats] = useState(true);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPasswordData, setNewPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    admins: 0,
    support: 0,
    clients: 0
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Iniciando carregamento de usuários...');
      const userData = await User.list();
      console.log('📊 Dados de usuários recebidos:', userData);
      console.log('📊 Quantidade de usuários:', userData?.length || 0);
      console.log('📊 Exemplo de usuário:', userData?.[0]);
      
      setUsers(userData);
      
      // Calculate stats
      const stats = {
        total: userData.length,
        active: userData.length, // Considerar todos como ativos já que não temos campo status
        inactive: 0, // Não temos campo status
        admins: userData.filter(u => u.role === 'admin').length,
        support: userData.filter(u => u.role === 'support').length,
        clients: userData.filter(u => u.role === 'client').length
      };
      console.log('📊 Estatísticas calculadas:', stats);
      setUserStats(stats);
      
      // Get current user
      const currentUserData = await User.me();
      console.log('👤 Usuário atual:', currentUserData);
      setCurrentUser(currentUserData);
    } catch (error) {
      console.error('❌ Erro ao carregar usuários:', error);
    }
    setIsLoading(false);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !filters.search || 
      user.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.email?.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesRole = filters.role === 'all' || user.role === filters.role;
    // Remover filtro por status já que não existe na tabela user_profiles
    // const matchesStatus = filters.status === 'all' || user.status === filters.status;
    
    return matchesSearch && matchesRole; // && matchesStatus;
  }).sort((a, b) => {
    if (filters.sortBy === 'name') {
      return filters.sortOrder === 'asc' 
        ? a.name?.localeCompare(b.name || '')
        : b.name?.localeCompare(a.name || '');
    } else if (filters.sortBy === 'date') {
      return filters.sortOrder === 'asc'
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });

  const handleDeleteUser = async (userId) => {
    // In a real application, this would call an API to delete or deactivate the user
    alert(`Em uma aplicação real, esta ação desativaria o usuário ID: ${userId}`);
    setConfirmDeleteUser(null);
  };

  const handleInactivateUser = async (userId) => {
    // Buscar o usuário pelo id para garantir que é o id da tabela user_profiles
    const user = users.find(u => u.id === userId);
    if (!user) {
      alert('Usuário não encontrado!');
      return;
    }
    if (!window.confirm('Tem certeza que deseja inativar este usuário?')) return;
    try {
      await User.update(user.id, { is_active: false });
      // Recarregar lista de usuários
      await loadUsers();
      alert('Usuário inativado com sucesso!');
    } catch (error) {
      alert('Erro ao inativar usuário: ' + (error.message || error));
    }
  };

  const handleResetPassword = async (userId, userName) => {
    // Abrir modal para redefinir senha
    setResetPasswordUser({ id: userId, name: userName });
    setNewPasswordData({ password: '', confirmPassword: '' });
  };

  const confirmResetPassword = async () => {
    if (!newPasswordData.password || newPasswordData.password.length < 6) {
      alert('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (newPasswordData.password !== newPasswordData.confirmPassword) {
      alert('As senhas não coincidem.');
      return;
    }

    setIsResettingPassword(true);
    try {
      console.log(`🔐 Redefinindo senha para usuário: ${resetPasswordUser.name}`);
      
      // Atualizar senha do usuário
      await User.update(resetPasswordUser.id, {
        password: newPasswordData.password
      });

      console.log('✅ Senha redefinida com sucesso!');
      alert(`✅ Senha redefinida com sucesso para ${resetPasswordUser.name}!\n\nO usuário poderá usar a nova senha no próximo login.`);
      setResetPasswordUser(null);
      setNewPasswordData({ password: '', confirmPassword: '' });
    } catch (error) {
      console.error('❌ Erro ao redefinir senha:', error);
      alert(`❌ Erro ao redefinir senha: ${error.message}\n\nTente novamente ou contacte o administrador do sistema.`);
    }
    setIsResettingPassword(false);
  };

  const cancelResetPassword = () => {
    setResetPasswordUser(null);
    setNewPasswordData({ password: '', confirmPassword: '' });
  };

  const UserCard = ({ user }) => {
    const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.user;
    // Determinar status: usar user.status, senão user.is_active
    let statusKey = 'active';
    if (user.status) statusKey = user.status;
    else if (user.is_active === false) statusKey = 'inactive';
    const statusConfig = STATUS_CONFIG[statusKey] || STATUS_CONFIG.active;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.2 }}
      >
        <Card className="h-full hover:shadow-md transition-all duration-200 group">
          <CardHeader className="p-6 pb-4">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12 border-2 border-primary/20">
                  <AvatarImage src={user.avatar_url} alt={user.name} />
                  <AvatarFallback className="bg-primary/10 text-primary font-medium">
                    {user.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="group-hover:text-primary transition-colors">
                    {user.name || 'Sem nome'}
                  </CardTitle>
                  <CardDescription className="flex items-center mt-1">
                    <Mail className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                    {user.email}
                  </CardDescription>
                  <div className="flex items-center mt-2">
                    <Badge className={statusConfig.color + ' flex items-center gap-1'}>
                      {statusConfig.icon}
                      {statusConfig.label}
                    </Badge>
                  </div>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => navigate(createPageUrl(`ViewUser?id=${user.id}`))}
                    className="cursor-pointer"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ver Perfil
                  </DropdownMenuItem>
                                    <DropdownMenuItem 
                    onClick={() => navigate(createPageUrl(`EditUser?id=${user.id}`))}
                    className="cursor-pointer"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => handleResetPassword(user.id, user.name)}
                    className="cursor-pointer"
                  >
                    <svg 
                      className="w-4 h-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" 
                      />
                    </svg>
                    Redefinir Senha
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => handleInactivateUser(user.id)}
                    className="cursor-pointer text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Desativar Usuário
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-2">
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className={roleConfig.color}>
                {roleConfig.icon}
                {roleConfig.label}
              </Badge>
            </div>
            
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 mr-2" />
              Membro desde {user.created_at && !isNaN(new Date(user.created_at)) ? format(new Date(user.created_at), 'dd/MM/yyyy', { locale: ptBR }) : 'Data não disponível'}
            </div>
          </CardContent>
          <CardFooter className="px-6 py-4 flex justify-between mt-auto">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(createPageUrl(`ViewUser?id=${user.id}`))}
            >
              Ver Perfil
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => window.location.href = `mailto:${user.email}`}
            >
              <Mail className="h-4 w-4 mr-2" />
              Enviar Email
            </Button>
          </CardFooter>
        </Card>
      </motion.div>
    );
  };

  const UserTable = () => (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Usuário</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Função</TableHead>
              <TableHead>Criado em</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <AnimatePresence>
              {filteredUsers.map((user) => {
                const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.user;
                const statusConfig = STATUS_CONFIG[user.status || 'active'] || STATUS_CONFIG.active;
                
                return (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(createPageUrl(`ViewUser?id=${user.id}`))}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={user.avatar_url} alt={user.name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-medium">
                            {user.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.name || 'Sem nome'}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfig.color + ' flex items-center gap-1'}>
                        {statusConfig.icon}
                        {statusConfig.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <Badge className={roleConfig.color}>
                            {roleConfig.icon}
                            {roleConfig.label}
                          </Badge>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80">
                          <div className="flex justify-between space-x-4">
                            <div className="space-y-1">
                              <h4 className="text-sm font-semibold">Função: {roleConfig.label}</h4>
                              <p className="text-sm text-muted-foreground">
                                {roleConfig.description}
                              </p>
                            </div>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </TableCell>
                    <TableCell>
                                              {user.created_at && !isNaN(new Date(user.created_at)) ? format(new Date(user.created_at), 'dd/MM/yyyy', { locale: ptBR }) : 'Data não disponível'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(createPageUrl(`EditUser?id=${user.id}`));
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResetPassword(user.id, user.name);
                          }}
                          title="Redefinir Senha"
                        >
                          <svg 
                            className="w-4 h-4" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" 
                            />
                          </svg>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInactivateUser(user.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </motion.tr>
                );
              })}
            </AnimatePresence>
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                    <UsersIcon className="h-10 w-10 mb-2 opacity-20" />
                    <p>Nenhum usuário encontrado</p>
                    <p className="text-sm">Tente alterar os filtros</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <UsersIcon className="h-8 w-8 text-primary" />
            Usuários
          </h1>
          <p className="text-muted-foreground">
            Gerencie os usuários do sistema
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <ExportButton
            data={filteredUsers}
            filename="usuarios_export"
            title="Relatório de Usuários"
            type="users"
            variant="outline"
            size="sm"
            disabled={isLoading}
          />
          <Button 
            onClick={() => navigate(createPageUrl('NewUser'))}
            className="bg-primary"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Novo Usuário
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowStats(!showStats)}
          >
            <BarChart className="h-4 w-4 mr-2" />
            {showStats ? 'Ocultar Estatísticas' : 'Mostrar Estatísticas'}
          </Button>
        </div>
      </div>

      {showStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <p className="text-sm text-muted-foreground font-medium mb-1">Total de Usuários</p>
              <p className="text-3xl font-bold text-primary">{userStats.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <p className="text-sm text-muted-foreground font-medium mb-1">Usuários Ativos</p>
              <p className="text-3xl font-bold text-green-600">{userStats.active}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <p className="text-sm text-muted-foreground font-medium mb-1">Usuários Inativos</p>
              <p className="text-3xl font-bold text-red-600">{userStats.inactive}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <p className="text-sm text-muted-foreground font-medium mb-1">Administradores</p>
              <p className="text-3xl font-bold text-amber-600">{userStats.admins}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <p className="text-sm text-muted-foreground font-medium mb-1">Suporte</p>
              <p className="text-3xl font-bold text-blue-600">{userStats.support}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <p className="text-sm text-muted-foreground font-medium mb-1">Clientes</p>
              <p className="text-3xl font-bold text-green-600">{userStats.clients}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar usuários..."
              className="pl-9"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              disabled={isLoading}
            />
          </div>
          
          <Select
            value={filters.role}
            onValueChange={(value) => setFilters({ ...filters, role: value })}
            disabled={isLoading}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Função" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Funções</SelectItem>
              <SelectItem value="admin">Administradores</SelectItem>
              <SelectItem value="manager">Gerentes</SelectItem>
              <SelectItem value="support">Suporte</SelectItem>
              <SelectItem value="dev">Desenvolvedores</SelectItem>
              <SelectItem value="client">Clientes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onValueChange={(value) => {
              const [sortBy, sortOrder] = value.split('-');
              setFilters({ ...filters, sortBy, sortOrder });
            }}
            disabled={isLoading}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name-asc">Nome (A-Z)</SelectItem>
              <SelectItem value="name-desc">Nome (Z-A)</SelectItem>
              <SelectItem value="date-asc">Data (Antigos)</SelectItem>
              <SelectItem value="date-desc">Data (Recentes)</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex border rounded-md overflow-hidden">
            <Button 
              variant={viewMode === 'grid' ? "default" : "ghost"}
              className={`px-3 py-2 rounded-none ${viewMode === 'grid' ? 'bg-primary' : ''}`}
              size="icon"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button 
              variant={viewMode === 'list' ? "default" : "ghost"}
              className={`px-3 py-2 rounded-none ${viewMode === 'list' ? 'bg-primary' : ''}`}
              size="icon"
              onClick={() => setViewMode('list')}
            >
              <LayoutList className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardHeader className="p-6 pb-4">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-6 pb-2">
                  <div className="flex gap-2 mb-4">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </CardContent>
                <CardFooter className="px-6 py-4 flex justify-between">
                  <Skeleton className="h-9 w-24" />
                  <Skeleton className="h-9 w-24" />
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Skeleton className="h-9 w-9 rounded-md" />
                      <Skeleton className="h-9 w-9 rounded-md" />
                      <Skeleton className="h-9 w-9 rounded-md" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )
      ) : (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {filteredUsers.map((user) => (
                <UserCard key={user.id} user={user} />
              ))}
            </AnimatePresence>
            {filteredUsers.length === 0 &&
              <Card className="col-span-full">
                <CardContent className="flex flex-col items-center justify-center py-10 text-center">
                  <UsersIcon className="h-16 w-16 text-muted-foreground opacity-20 mb-4" />
                  <h3 className="text-lg font-medium">Nenhum usuário encontrado</h3>
                  <p className="text-muted-foreground mt-2">
                    Tente modificar seus filtros de busca ou adicione novos usuários ao sistema.
                  </p>
                  <Button 
                    className="mt-6" 
                    onClick={() => navigate(createPageUrl('NewUser'))}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Novo Usuário
                  </Button>
                </CardContent>
              </Card>
            }
          </div>
        ) : (
          <UserTable />
        )
      )}
      
      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={cancelResetPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Redefinir Senha
            </DialogTitle>
            <DialogDescription>
              Defina uma nova senha para <strong>{resetPasswordUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Digite a nova senha (mín. 6 caracteres)"
                value={newPasswordData.password}
                onChange={(e) => setNewPasswordData(prev => ({ 
                  ...prev, 
                  password: e.target.value 
                }))}
                disabled={isResettingPassword}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar Nova Senha</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Confirme a nova senha"
                value={newPasswordData.confirmPassword}
                onChange={(e) => setNewPasswordData(prev => ({ 
                  ...prev, 
                  confirmPassword: e.target.value 
                }))}
                disabled={isResettingPassword}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={cancelResetPassword}
              disabled={isResettingPassword}
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmResetPassword}
              disabled={isResettingPassword}
            >
              {isResettingPassword ? 'Redefinindo...' : 'Redefinir Senha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Confirm Delete Dialog */}
      <AlertDialog open={!!confirmDeleteUser} onOpenChange={() => setConfirmDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá desativar o usuário <strong>{confirmDeleteUser?.name}</strong> ({confirmDeleteUser?.email}). 
              <br /><br />
              O usuário não poderá mais acessar o sistema, mas seus dados e histórico serão mantidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground"
              onClick={() => handleDeleteUser(confirmDeleteUser?.id)}
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}